import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Defect, DefectCode } from '../../types/defect';
import { DataTableFilterMeta } from 'primereact/datatable';
import { defaultDefectFilters } from '../../utils/defectTableHelper';
import { TablePageState } from '../../types/table';

export interface DefectState {
    data: Defect[];
    loading: boolean;
    error: boolean;
    globalFilterValue: string;
    filters: DataTableFilterMeta;
    page: TablePageState;
    toEdit: Defect | undefined;
    toEditImages: Defect | undefined;
    toEditDefectCodes: Defect | undefined;
    defectCodes: DefectCode[];
    toDelete: Defect[];
    showDeleteSelected: boolean;
    failed: number[];
    filteredDefects: Defect[];
}

export const initialState: DefectState = {
    data: [],
    loading: true,
    error: false,
    globalFilterValue: "",
    filters: defaultDefectFilters,
    page: { row: 50, first: 0 },
    toEdit: undefined,
    toEditImages: undefined,
    toEditDefectCodes: undefined,
    defectCodes: [],
    toDelete: [],
    showDeleteSelected: false,
    failed: [],
    filteredDefects: [],
};

export const defectSlice = createSlice({
    name: 'defect',
    initialState,
    reducers: {
        setData: (state, action: PayloadAction<Defect[]>) => {
            state.data = action.payload;
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        },
        setError: (state, action: PayloadAction<boolean>) => {
            state.error = action.payload;
        },
        setGlobalFilterValue: (state, action: PayloadAction<string>) => {
            state.globalFilterValue = action.payload;
        },
        setFilters: (state, action: PayloadAction<DataTableFilterMeta>) => {
            state.filters = action.payload;
        },
        setPage: (state, action: PayloadAction<TablePageState>) => {
            state.page = action.payload;
        },
        setToEdit: (state, action: PayloadAction<Defect | undefined>) => {
            state.toEdit = action.payload;
        },
        setToEditImages: (state, action: PayloadAction<Defect | undefined>) => {
            state.toEditImages = action.payload;
        },
        setToEditDefectCodes: (state, action: PayloadAction<Defect | undefined>) => {
            state.toEditDefectCodes = action.payload;
        },
        setDefectCodes: (state, action: PayloadAction<DefectCode[]>) => {
            state.defectCodes = action.payload;
        },
        setToDelete: (state, action: PayloadAction<Defect[]>) => {
            state.toDelete = action.payload;
        },
        setShowDeleteSelected: (state, action: PayloadAction<boolean>) => {
            state.showDeleteSelected = action.payload;
        },
        addItem: (state, action: PayloadAction<Defect>) => {
            state.data = state.data.concat([action.payload]);
            state.toEdit = undefined;
        },
        updateItem: (state, action: PayloadAction<Defect>) => {
            state.data = state.data.map((defect) => {
                if (defect.defectId === action.payload.defectId) {
                    return action.payload;
                }

                return defect;
            });
            state.toEdit = undefined;
        },
        deleteItem: (state, action: PayloadAction<Defect[]>) => {
            const defectIdsToDelete = action.payload.map(defect => defect.defectId);
            state.data = state.data.filter((defect) => !defectIdsToDelete.includes(defect.defectId));
            state.toDelete = [];
        },
        setFailed: (state, action: PayloadAction<number[]>) => {
            state.failed = state.failed.concat(action.payload);
        },
        setFilteredDefects: (state, action: PayloadAction<Defect[]>) => {
            state.filteredDefects = action.payload;
        },
    },
});

export const { 
    setData,
    setLoading,
    setError,
    setGlobalFilterValue,
    setFilters,
    setPage,
    setToEdit,
    setToEditImages,
    setToEditDefectCodes,
    setDefectCodes,
    setToDelete,
    setShowDeleteSelected,
    addItem,
    updateItem,
    deleteItem,
    setFailed,
    setFilteredDefects,
 } = defectSlice.actions;

export default defectSlice.reducer;
